import { useState, useRef, useCallback, useEffect } from 'react';
import { getSpineManager } from '../../lib/spine-manager';
import { useSceneStore } from '../../stores/sceneStore';
import { useToolStore } from '../../stores/toolStore';
import { worldToLocal } from '../../lib/transform-utils';
import type { Layer, Transform } from '@spine-workspace/shared';
import type { ViewportState } from '../../lib/spine-manager';

const PHYSICS_DRAG_SCALE = 1.2;  // screen px/event → wind/gravity units
const PHYSICS_DECAY = 0.82;      // multiplier per rAF frame (~0.4s to near-zero at 60fps)
const PHYSICS_STOP = 0.08;       // stop decay below this magnitude

const HANDLE_SIZE = 8;
const ROTATE_OFFSET = 28; // px above AABB top edge
const RULER_SIZE = 20;    // must match SceneCanvas

const ROTATE_CURSOR =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' " +
  "viewBox='0 0 20 20'%3E%3Cpath d='M 16 10 A 6 6 0 1 0 10 16' fill='none' stroke='white' " +
  "stroke-width='3.5' stroke-linecap='round'/%3E%3Cpath d='M 10 16 L 13 13.5 L 13 18.5 Z' " +
  "fill='white'/%3E%3Cpath d='M 16 10 A 6 6 0 1 0 10 16' fill='none' stroke='black' " +
  "stroke-width='2' stroke-linecap='round'/%3E%3Cpath d='M 10 16 L 13 13.5 L 13 18.5 Z' " +
  "fill='black'/%3E%3C/svg%3E\") 10 10, crosshair";

type DragMode =
  | 'move'
  | 'scale-tl' | 'scale-tc' | 'scale-tr'
  | 'scale-ml' | 'scale-mr'
  | 'scale-bl' | 'scale-bc' | 'scale-br'
  | 'rotate';

interface DragState {
  mode: DragMode;
  startClientX: number;
  startClientY: number;
  /** Client-space position of the rotation pivot (= spine root, not AABB center). */
  pivotClientX: number;
  pivotClientY: number;
  startX: number;
  startY: number;
  startScaleX: number;
  startScaleY: number;
  startRotation: number;
  startBoundsW: number;
  startBoundsH: number;
  xAnchorLocal: number | null;
  yAnchorLocal: number | null;
}

type Bounds = { x: number; y: number; width: number; height: number };

interface TransformGizmoProps {
  layer: Layer;
  /** Always world-space transform (composed up the hierarchy). Used for pivot CSS + drag start. */
  worldTransform: Transform;
  /** Parent's world-space transform — null for root layers. Used to convert world→local on drag. */
  parentWorldTransform: Transform | null;
  /** Bounds in PixiJS screen (stage) space — before RULER_SIZE offset */
  bounds: Bounds;
  vpState: ViewportState;
}

export default function TransformGizmo({ layer, worldTransform, parentWorldTransform, bounds, vpState }: TransformGizmoProps) {
  const { updateLayerTransform, pushUndo } = useSceneStore();
  const activeTool  = useToolStore((s) => s.activeTool);
  const snapEnabled = useToolStore((s) => s.snapEnabled);
  const canvasWidth  = useSceneStore((s) => s.canvasWidth);
  const canvasHeight = useSceneStore((s) => s.canvasHeight);
  const layers = useSceneStore((s) => s.layers);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [rotationDeg, setRotationDeg] = useState(0);
  const [snapLines, setSnapLines] = useState<{ x: number | null; y: number | null }>({ x: null, y: null });
  const wrapperRef = useRef<HTMLDivElement>(null); // zero-size div at the pivot point
  const boxRef    = useRef<HTMLDivElement>(null);  // for data-testid / Playwright

  const frozenBoundsRef = useRef<Bounds | null>(null);
  const [displayBounds, setDisplayBounds] = useState<Bounds | null>(null);

  // Physics drag-simulation refs
  const prevDragPosRef = useRef<{ x: number; y: number } | null>(null);
  const physicsRef = useRef({ wind: 0, gravity: 0 });
  const decayRafRef = useRef<number>(0);

  // Cleanup decay on unmount
  useEffect(() => () => { cancelAnimationFrame(decayRafRef.current); }, []);

  function applyPhysics(wind: number, gravity: number) {
    physicsRef.current = { wind, gravity };
    getSpineManager().setLayerPhysicsWind(layer.id, wind);
    getSpineManager().setLayerPhysicsGravity(layer.id, gravity);
  }

  function startPhysicsDecay() {
    cancelAnimationFrame(decayRafRef.current);
    function decay() {
      let { wind, gravity } = physicsRef.current;
      wind *= PHYSICS_DECAY;
      gravity *= PHYSICS_DECAY;
      if (Math.abs(wind) < PHYSICS_STOP && Math.abs(gravity) < PHYSICS_STOP) {
        applyPhysics(0, 0);
        return;
      }
      applyPhysics(wind, gravity);
      decayRafRef.current = requestAnimationFrame(decay);
    }
    decayRafRef.current = requestAnimationFrame(decay);
  }

  const { zoom, x: vpX, y: vpY } = vpState;

  const visibleBounds = displayBounds ?? bounds;

  // AABB top-left in container CSS space
  const L = visibleBounds.x + RULER_SIZE;
  const T = visibleBounds.y + RULER_SIZE;
  const W = Math.max(visibleBounds.width,  4);
  const H = Math.max(visibleBounds.height, 4);

  // Spine root in container CSS space — always use world-space coordinates so the
  // pivot is correct even when the layer has a parent with rotation/scale.
  const pivotCSSX = worldTransform.x * zoom + vpX + RULER_SIZE;
  const pivotCSSY = worldTransform.y * zoom + vpY + RULER_SIZE;

  // AABB corners relative to the pivot (used for child element positioning)
  const bboxLeft    = L - pivotCSSX;
  const bboxTop     = T - pivotCSSY;
  const bboxCenterX = bboxLeft + W / 2;

  const startDrag = useCallback((e: React.PointerEvent, mode: DragMode) => {
    e.preventDefault();
    e.stopPropagation();
    (e.target as Element).setPointerCapture(e.pointerId);
    pushUndo();

    frozenBoundsRef.current = bounds;
    setDisplayBounds(bounds);

    const pivotRect = wrapperRef.current!.getBoundingClientRect();

    // Anchor offsets computed from world-space pivot so scale drag anchors are correct
    const worldBboxLeft   = (bounds.x - vpX) / zoom;
    const worldBboxRight  = worldBboxLeft + bounds.width / zoom;
    const worldBboxTop    = (bounds.y - vpY) / zoom;
    const worldBboxBottom = worldBboxTop + bounds.height / zoom;

    let xAnchorLocal: number | null = null;
    let yAnchorLocal: number | null = null;
    if (mode === 'scale-tr' || mode === 'scale-mr' || mode === 'scale-br')
      xAnchorLocal = worldBboxLeft   - worldTransform.x;
    if (mode === 'scale-tl' || mode === 'scale-ml' || mode === 'scale-bl')
      xAnchorLocal = worldBboxRight  - worldTransform.x;
    if (mode === 'scale-bl' || mode === 'scale-bc' || mode === 'scale-br')
      yAnchorLocal = worldBboxTop    - worldTransform.y;
    if (mode === 'scale-tl' || mode === 'scale-tc' || mode === 'scale-tr')
      yAnchorLocal = worldBboxBottom - worldTransform.y;

    setDrag({
      mode,
      startClientX: e.clientX,
      startClientY: e.clientY,
      pivotClientX: pivotRect.left,
      pivotClientY: pivotRect.top,
      startX: worldTransform.x,       // always world-space
      startY: worldTransform.y,
      startScaleX: worldTransform.scaleX,
      startScaleY: worldTransform.scaleY,
      startRotation: worldTransform.rotation,
      startBoundsW: bounds.width  / zoom,
      startBoundsH: bounds.height / zoom,
      xAnchorLocal,
      yAnchorLocal,
    });
    getSpineManager().pauseViewportDrag();
    // Cancel any in-progress decay when a new drag starts
    cancelAnimationFrame(decayRafRef.current);
    prevDragPosRef.current = null;
  }, [worldTransform, bounds, zoom, vpX, vpY, pushUndo]);

  /**
   * Convert a world-space partial transform to the coordinate space expected by the store.
   * Layers with a parent store LOCAL transforms; root layers store WORLD transforms.
   */
  const toStoreTransform = useCallback((worldUpdates: Partial<Transform>): Partial<Transform> => {
    if (!parentWorldTransform) return worldUpdates; // root layer — already world space

    // Build a full world transform by merging updates onto the current world transform
    const merged: Transform = { ...worldTransform, ...worldUpdates };
    const local = worldToLocal(merged, parentWorldTransform);

    // Return only the keys that were actually in worldUpdates
    const result: Partial<Transform> = {};
    if ('x' in worldUpdates || 'y' in worldUpdates) { result.x = local.x; result.y = local.y; }
    if ('scaleX' in worldUpdates) result.scaleX = local.scaleX;
    if ('scaleY' in worldUpdates) result.scaleY = local.scaleY;
    if ('rotation' in worldUpdates) result.rotation = local.rotation;
    return result;
  }, [worldTransform, parentWorldTransform]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!drag || !frozenBoundsRef.current) return;
    const fb = frozenBoundsRef.current;
    const {
      mode, startClientX, startClientY, pivotClientX, pivotClientY,
      startX, startY, startScaleX, startScaleY, startRotation,
      startBoundsW, startBoundsH, xAnchorLocal, yAnchorLocal,
    } = drag;

    const dx = e.clientX - startClientX;
    const dy = e.clientY - startClientY;

    if (mode === 'move') {
      // Physics: compute velocity from position delta and drive wind/gravity
      if (prevDragPosRef.current) {
        const velX = e.clientX - prevDragPosRef.current.x;
        const velY = e.clientY - prevDragPosRef.current.y;
        const wind    = Math.max(-50, Math.min(50, velX * PHYSICS_DRAG_SCALE));
        const gravity = Math.max(-50, Math.min(50, velY * PHYSICS_DRAG_SCALE));
        applyPhysics(wind, gravity);
      }
      prevDragPosRef.current = { x: e.clientX, y: e.clientY };

      const GRID_SNAP = 100; // world units — matches grid lines
      const SMART_THRESHOLD = 8 / zoom; // 8 px in screen space → world units
      const rawX = startX + dx / zoom;
      const rawY = startY + dy / zoom;

      let newX = snapEnabled ? Math.round(rawX / GRID_SNAP) * GRID_SNAP : rawX;
      let newY = snapEnabled ? Math.round(rawY / GRID_SNAP) * GRID_SNAP : rawY;
      let snapLineX: number | null = null;
      let snapLineY: number | null = null;

      if (!snapEnabled) {
        // Smart snap points: canvas center, canvas edges, other layer origins
        const hw = canvasWidth / 2;
        const hh = canvasHeight / 2;
        const snapCandidatesX = [0, -hw, hw, ...layers.filter((l) => l.id !== layer.id).map((l) => l.transform.x)];
        const snapCandidatesY = [0, -hh, hh, ...layers.filter((l) => l.id !== layer.id).map((l) => l.transform.y)];

        for (const cx of snapCandidatesX) {
          if (Math.abs(rawX - cx) < SMART_THRESHOLD) { newX = cx; snapLineX = cx; break; }
        }
        for (const cy of snapCandidatesY) {
          if (Math.abs(rawY - cy) < SMART_THRESHOLD) { newY = cy; snapLineY = cy; break; }
        }
      }

      setSnapLines({ x: snapLineX, y: snapLineY });
      updateLayerTransform(layer.id, toStoreTransform({ x: newX, y: newY }));
      const snapDx = (newX - startX) * zoom;
      const snapDy = (newY - startY) * zoom;
      setDisplayBounds({ x: fb.x + snapDx, y: fb.y + snapDy, width: fb.width, height: fb.height });
      return;
    }

    if (mode === 'rotate') {
      const startAngle = Math.atan2(startClientY - pivotClientY, startClientX - pivotClientX);
      const curAngle   = Math.atan2(e.clientY    - pivotClientY, e.clientX    - pivotClientX);
      let delta = (curAngle - startAngle) * (180 / Math.PI);
      if (e.shiftKey) delta = Math.round(delta / 15) * 15;
      updateLayerTransform(layer.id, toStoreTransform({ rotation: startRotation + delta }));
      setRotationDeg(delta);
      return;
    }

    // Scale
    const wdx = dx / zoom;
    const wdy = dy / zoom;
    let newScaleX = startScaleX;
    let newScaleY = startScaleY;

    switch (mode) {
      case 'scale-tr': case 'scale-mr': case 'scale-br':
        newScaleX = startScaleX * Math.max(0.01, (startBoundsW + wdx) / startBoundsW); break;
      case 'scale-tl': case 'scale-ml': case 'scale-bl':
        newScaleX = startScaleX * Math.max(0.01, (startBoundsW - wdx) / startBoundsW); break;
    }
    switch (mode) {
      case 'scale-bl': case 'scale-bc': case 'scale-br':
        newScaleY = startScaleY * Math.max(0.01, (startBoundsH + wdy) / startBoundsH); break;
      case 'scale-tl': case 'scale-tc': case 'scale-tr':
        newScaleY = startScaleY * Math.max(0.01, (startBoundsH - wdy) / startBoundsH); break;
    }

    // Shift — lock aspect ratio (uniform scale)
    if (e.shiftKey) {
      const rX = newScaleX / startScaleX;
      const rY = newScaleY / startScaleY;
      const r = Math.abs(rX - 1) >= Math.abs(rY - 1) ? rX : rY;
      newScaleX = startScaleX * r;
      newScaleY = startScaleY * r;
    }

    const worldUpdates: Partial<Transform> = { scaleX: newScaleX, scaleY: newScaleY };
    if (xAnchorLocal !== null) worldUpdates.x = startX + xAnchorLocal * (1 - newScaleX / startScaleX);
    if (yAnchorLocal !== null) worldUpdates.y = startY + yAnchorLocal * (1 - newScaleY / startScaleY);
    updateLayerTransform(layer.id, toStoreTransform(worldUpdates));

    const rX = newScaleX / startScaleX;
    const rY = newScaleY / startScaleY;
    const newW = Math.max(fb.width * rX, 4);
    const newH = Math.max(fb.height * rY, 4);
    let dispX = fb.x;
    let dispY = fb.y;
    if (mode === 'scale-tl' || mode === 'scale-ml' || mode === 'scale-bl') dispX = fb.x + fb.width  - newW;
    if (mode === 'scale-tl' || mode === 'scale-tc' || mode === 'scale-tr') dispY = fb.y + fb.height - newH;
    setDisplayBounds({ x: dispX, y: dispY, width: newW, height: newH });
  }, [drag, zoom, layer.id, updateLayerTransform, toStoreTransform]);

  const onPointerUp = useCallback(() => {
    if (!drag) return;
    frozenBoundsRef.current = null;
    setDisplayBounds(null);
    setRotationDeg(0);
    setDrag(null);
    setSnapLines({ x: null, y: null });
    getSpineManager().resumeViewportDrag();
    // Start physics decay when drag ends
    if (drag.mode === 'move') {
      prevDragPosRef.current = null;
      startPhysicsDecay();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag]);

  const scaleHandle = (pivotRelX: number, pivotRelY: number, mode: DragMode, cursor: string) => {
    const corner = mode.replace('scale-', '');
    return (
      <div
        key={mode}
        data-testid={`gizmo-handle-${corner}`}
        className="absolute bg-white border border-accent rounded-sm shadow-sm"
        style={{
          left: pivotRelX - HANDLE_SIZE / 2,
          top:  pivotRelY - HANDLE_SIZE / 2,
          width: HANDLE_SIZE,
          height: HANDLE_SIZE,
          cursor,
          zIndex: 2,
          touchAction: 'none',
        }}
        onPointerDown={(e) => startDrag(e, mode)}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      />
    );
  };

  // Snap lines in screen space (not inside the rotated wrapper)
  const snapLineXPx = snapLines.x !== null ? snapLines.x * zoom + vpX + RULER_SIZE : null;
  const snapLineYPx = snapLines.y !== null ? snapLines.y * zoom + vpY + RULER_SIZE : null;

  return (
    <>
    {snapLineXPx !== null && (
      <div
        data-testid="snap-line-x"
        className="absolute pointer-events-none"
        style={{ left: snapLineXPx, top: 0, width: 1, bottom: 0, background: 'rgba(99,240,180,0.8)', zIndex: 50 }}
      />
    )}
    {snapLineYPx !== null && (
      <div
        data-testid="snap-line-y"
        className="absolute pointer-events-none"
        style={{ top: snapLineYPx, left: 0, height: 1, right: 0, background: 'rgba(99,240,180,0.8)', zIndex: 50 }}
      />
    )}
    {/* Zero-size wrapper sitting at the spine root (CSS space).
        CSS rotate() here matches PixiJS rotation exactly — both pivot on the spine root. */}
    <div
      ref={wrapperRef}
      className="absolute"
      style={{
        left: pivotCSSX,
        top:  pivotCSSY,
        width: 0,
        height: 0,
        transform: rotationDeg !== 0 ? `rotate(${rotationDeg}deg)` : undefined,
        zIndex: 40,
      }}
    >
      {/* Bounding box — offset from pivot to AABB top-left */}
      <div
        ref={boxRef}
        data-testid="gizmo-box"
        className="absolute"
        style={{
          left: bboxLeft,
          top:  bboxTop,
          width: W,
          height: H,
          border: '1px dashed rgba(99,102,241,0.85)',
          cursor: drag?.mode === 'move' ? 'grabbing' : 'move',
          boxSizing: 'border-box',
          touchAction: 'none',
          zIndex: 1,
        }}
        onPointerDown={(e) => startDrag(e, 'move')}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      />

      {/* Rotation connector line + handle — hidden in move/scale mode */}
      {(activeTool === 'select' || activeTool === 'rotate') && (
        <>
          <div
            className="absolute pointer-events-none"
            style={{
              left: bboxCenterX - 0.5,
              top:  bboxTop - ROTATE_OFFSET,
              width: 1,
              height: ROTATE_OFFSET,
              background: 'rgba(99,102,241,0.55)',
              zIndex: 1,
            }}
          />
          <div
            data-testid="gizmo-rotate"
            className="absolute rounded-full bg-accent border-2 border-white shadow-sm"
            style={{
              left: bboxCenterX - 6,
              top:  bboxTop - ROTATE_OFFSET - 6,
              width: 12,
              height: 12,
              cursor: ROTATE_CURSOR,
              zIndex: 2,
              touchAction: 'none',
            }}
            onPointerDown={(e) => startDrag(e, 'rotate')}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
          />
        </>
      )}

      {/* Scale handles — hidden in move/rotate mode */}
      {(activeTool === 'select' || activeTool === 'scale') && (
        <>
          {scaleHandle(bboxLeft,           bboxTop,           'scale-tl', 'nwse-resize')}
          {scaleHandle(bboxCenterX,        bboxTop,           'scale-tc', 'ns-resize')}
          {scaleHandle(bboxLeft + W,       bboxTop,           'scale-tr', 'nesw-resize')}
          {scaleHandle(bboxLeft,           bboxTop + H / 2,   'scale-ml', 'ew-resize')}
          {scaleHandle(bboxLeft + W,       bboxTop + H / 2,   'scale-mr', 'ew-resize')}
          {scaleHandle(bboxLeft,           bboxTop + H,       'scale-bl', 'nesw-resize')}
          {scaleHandle(bboxCenterX,        bboxTop + H,       'scale-bc', 'ns-resize')}
          {scaleHandle(bboxLeft + W,       bboxTop + H,       'scale-br', 'nwse-resize')}
        </>
      )}
    </div>
    </>
  );
}
